// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	createStringType,
	Int32Type,
	SnowflakeStringType,
	SnowflakeType,
} from '@fluxer/schema/src/primitives/SchemaPrimitives';
import {z} from 'zod';

// Micup akis-kategori: feed categories the admins curate. Each lists the countries it is shown in (none: every
// country); the caller's country comes from the request (GeoIP), never from the client. The categories and the
// phrase list live in one instance configuration value, so the bounds below keep that value small.
export const FEED_CATEGORY_MAX = 50;
export const FEED_CATEGORY_NAME_MAX_LENGTH = 32;
export const FEED_CATEGORY_EMOJI_MAX_LENGTH = 16;
export const FEED_CATEGORY_MAX_POSITION = 1000;
export const FEED_CATEGORY_MAX_COUNTRIES = 50;
export const FEED_CATEGORY_MAX_BLOCKED_PHRASES = 500;
export const FEED_CATEGORY_BLOCKED_PHRASE_MIN_LENGTH = 3;
export const FEED_CATEGORY_BLOCKED_PHRASE_MAX_LENGTH = 64;

export const FeedCategoryCountryCode = z
	.string()
	.trim()
	.toUpperCase()
	.regex(/^[A-Z]{2}$/)
	.describe('ISO 3166-1 alpha-2 country code; stored in upper case');

export type FeedCategoryCountryCode = z.infer<typeof FeedCategoryCountryCode>;

export const FeedCategoryIdParam = z.object({
	category_id: SnowflakeType.describe('The ID of the feed category'),
});

export type FeedCategoryIdParam = z.infer<typeof FeedCategoryIdParam>;

export const FeedPostCategoryResponse = z.object({
	id: SnowflakeStringType.describe('The ID of the category'),
	name: z.string().describe('The name the admins gave the category'),
	emoji: z.string().describe('The emoji shown before the name'),
});

export type FeedPostCategoryResponse = z.infer<typeof FeedPostCategoryResponse>;

export const FeedCategoryResponse = FeedPostCategoryResponse.extend({});

export type FeedCategoryResponse = z.infer<typeof FeedCategoryResponse>;

export const FeedCategoryListResponse = z
	.array(FeedCategoryResponse)
	.describe("The enabled categories shown in the caller's country, by position");

export type FeedCategoryListResponse = z.infer<typeof FeedCategoryListResponse>;

const FeedCategoryName = createStringType(1, FEED_CATEGORY_NAME_MAX_LENGTH).describe(
	`Display name (1-${FEED_CATEGORY_NAME_MAX_LENGTH} characters), written in the language of its countries`,
);
const FeedCategoryEmoji = createStringType(1, FEED_CATEGORY_EMOJI_MAX_LENGTH).describe(
	'One emoji shown before the name',
);
const FeedCategoryPosition = z
	.number()
	.int()
	.min(0)
	.max(FEED_CATEGORY_MAX_POSITION)
	.describe(`Display order, lowest first (0-${FEED_CATEGORY_MAX_POSITION})`);
const FeedCategoryCountries = z
	.array(FeedCategoryCountryCode)
	.max(FEED_CATEGORY_MAX_COUNTRIES)
	.describe(`Countries the category is shown in, at most ${FEED_CATEGORY_MAX_COUNTRIES}; empty shows it everywhere`);

export const CreateFeedCategoryRequest = z.object({
	name: FeedCategoryName,
	emoji: FeedCategoryEmoji,
	position: FeedCategoryPosition.default(0),
	enabled: z.boolean().default(true).describe('Disabled categories are not listed and take no new posts'),
	countries: FeedCategoryCountries.default([]),
});

export type CreateFeedCategoryRequest = z.infer<typeof CreateFeedCategoryRequest>;

export const UpdateFeedCategoryRequest = z
	.object({
		name: FeedCategoryName.optional(),
		emoji: FeedCategoryEmoji.optional(),
		position: FeedCategoryPosition.optional(),
		enabled: z.boolean().optional(),
		countries: FeedCategoryCountries.optional(),
	})
	.refine((value) => Object.values(value).some((field) => field !== undefined), 'At least one field is required');

export type UpdateFeedCategoryRequest = z.infer<typeof UpdateFeedCategoryRequest>;

export const UpdateFeedCategoryPhrasesRequest = z.object({
	blocked_phrases: z
		.array(createStringType(FEED_CATEGORY_BLOCKED_PHRASE_MIN_LENGTH, FEED_CATEGORY_BLOCKED_PHRASE_MAX_LENGTH))
		.max(FEED_CATEGORY_MAX_BLOCKED_PHRASES)
		.describe(
			`The whole list, replacing the stored one (at most ${FEED_CATEGORY_MAX_BLOCKED_PHRASES}); each keeps at least 3 letters or digits once folded. A post filed under a category whose text, poll or image texts hold one at the start of a word is refused`,
		),
});

export type UpdateFeedCategoryPhrasesRequest = z.infer<typeof UpdateFeedCategoryPhrasesRequest>;

export const AdminFeedCategoryResponse = z.object({
	id: SnowflakeStringType.describe('The ID of the category'),
	name: z.string(),
	emoji: z.string(),
	position: Int32Type,
	enabled: z.boolean(),
	countries: z.array(z.string()).describe('Upper case country codes; empty shows the category everywhere'),
	created_at: z.string().describe('ISO8601 timestamp'),
});

export type AdminFeedCategoryResponse = z.infer<typeof AdminFeedCategoryResponse>;

export const AdminFeedCategoriesResponse = z.object({
	categories: z
		.array(AdminFeedCategoryResponse)
		.max(FEED_CATEGORY_MAX)
		.describe('Every category, disabled ones included, by position'),
	blocked_phrases: z.array(z.string()).describe('Phrases refused in posts filed under a category'),
});

export type AdminFeedCategoriesResponse = z.infer<typeof AdminFeedCategoriesResponse>;
